import { Module } from 'vuex'
import firebase from 'firebase/app'
import { BillingException } from '@/types/BillingException'
import { RootState } from '@/store'

export interface BillingExceptionState {
  billingException: BillingException | null
  unsubscribeFromBillingException: () => void
}

const BillingExceptionDocId = 'billingException'

const billingExceptionModule: Module<BillingExceptionState, RootState> = {
  namespaced: true,

  state: {
    billingException: null,
    unsubscribeFromBillingException: () => void {},
  },

  getters: {
    billingException: (state) => state.billingException,
  },

  mutations: {
    setBillingException(state, data: BillingException | null) {
      state.billingException = data
    },
    setUnsubscribeFromBillingException(state, data: () => void) {
      state.unsubscribeFromBillingException = data
    },
  },

  actions: {
    subscribeToBillingException: async ({ commit, dispatch }) => {
      try {
        commit('setLoading', true, { root: true })
        dispatch('unsubscribeFromBillingException')

        const billingExceptionDoc = firebase
          .firestore()
          .collection('settings')
          .doc(BillingExceptionDocId)

        const unsubsFunc = billingExceptionDoc.onSnapshot(
          (snapshot) => {
            if (snapshot.exists)
              commit('setBillingException', { id: snapshot.id, ...snapshot.data() })
            else commit('setBillingException', null)
          },
          (error) => {
            commit('setError', error.message, { root: true })
          }
        )

        commit('setUnsubscribeFromBillingException', unsubsFunc)
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },

    unsubscribeFromBillingException: ({ state, commit }) => {
      state.unsubscribeFromBillingException()
      commit('setUnsubscribeFromBillingException', () => void {})
    },

    createBillingException: async (
      { commit },
      newException: BillingException
    ): Promise<string | undefined> => {
      try {
        commit('setLoading', true, { root: true })

        const settingsCollection = firebase.firestore().collection('settings')
        const exceptionWithIds: BillingException = {
          emails: newException.emails.map((emailData) => ({
            id: settingsCollection.doc().id,
            email: emailData.email,
          })),
        }

        await settingsCollection.doc(BillingExceptionDocId).set(exceptionWithIds)
        commit('setSuccess', 'Billing exception successfully created!', { root: true })

        return BillingExceptionDocId
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },

    updateBillingException: async ({ commit }, newException: BillingException): Promise<void> => {
      try {
        commit('setLoading', true, { root: true })

        const settingsCollection = firebase.firestore().collection('settings')
        const exceptionWithIds: BillingException = {
          emails: newException.emails.map((emailData) =>
            emailData.id ? emailData : { id: settingsCollection.doc().id, email: emailData.email }
          ),
        }

        await settingsCollection.doc(BillingExceptionDocId).set(exceptionWithIds)

        commit('setSuccess', 'Billing exception successfully modified!', { root: true })
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },
  },
}

export default billingExceptionModule
